// frontend/src/pages/SignUpPage.js - Updated to match mobile experience
import React, { useState } from 'react';
import { Eye, EyeOff, ArrowLeft, Calendar } from 'lucide-react';

// Simple Input Component
const SimpleInput = ({ label, type = 'text', value, onChange, placeholder, error }) => (
  <div style={{ marginBottom: '20px' }}>
    <label style={{
      display: 'block',
      color: '#e5e7eb',
      fontSize: '14px',
      marginBottom: '8px'
    }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '12px',
        backgroundColor: '#374151',
        border: '1px solid ' + (error ? '#ef4444' : '#4b5563'),
        borderRadius: '8px',
        color: 'white',
        fontSize: '16px',
        outline: 'none',
        boxSizing: 'border-box'
      }}
      onFocus={(e) => e.target.style.borderColor = error ? '#ef4444' : '#3b82f6'}
      onBlur={(e) => e.target.style.borderColor = error ? '#ef4444' : '#4b5563'}
    />
    {error && (
      <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
        {error}
      </div>
    )}
  </div>
);

// Date validation function
const validateDateOfBirth = (date) => {
  if (!date) return 'Date of birth is required';
  
  const birthDate = new Date(date);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();
  
  let actualAge = age;
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    actualAge--;
  }
  
  if (actualAge < 18) return 'You must be 18 or older to register';
  if (birthDate > today) return 'Date of birth cannot be in the future';
  if (today.getFullYear() - birthDate.getFullYear() > 120) {
    return 'Please enter a valid date of birth';
  }
  
  return null;
};

const SignUpFlow = ({ onSignUp, onLogin, onBack }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Form state - matches mobile
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [email, setEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('Nigeria');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 0:
        if (!firstName.trim()) newErrors.firstName = 'First name is required';
        if (!lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
        break;
      case 1:
        if (!email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        
        const dobError = validateDateOfBirth(dateOfBirth);
        if (dobError) newErrors.dateOfBirth = dobError;
        
        if (!address.trim()) newErrors.address = 'Address is required';
        if (!state.trim()) newErrors.state = 'State is required';
        break;
      case 4:
        if (!username.trim()) newErrors.username = 'Username is required';
        if (!password) newErrors.password = 'Password is required';
        else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 4) {
        // Final step: register directly. The branded splash (started by
        // App.handleSignUp) covers the request + board load, then goes to the
        // dashboard - in sync with mobile (no scripted loader / success screen).
        completeSignUp();
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const prevStep = () => {
    // On the first step there's nothing to go back to within signup, so
    // return to the landing page instead of a dead-end
    if (currentStep === 0) {
      if (onBack) onBack();
      return;
    }
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };


  const handleOtpInput = (digit) => {
    if (otpCode.length < 5) {
      setOtpCode(otpCode + digit);
    }
  };

  const clearOtp = () => {
    setOtpCode(otpCode.slice(0, -1));
  };

  const verifyOtp = () => {
    if (otpCode.length === 5) {
      nextStep();
    }
  };

  const completeSignUp = async () => {
    if (onSignUp) {
      const result = await onSignUp({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim(),
        referralCode: referralCode.trim() || undefined,
        email: email.trim().toLowerCase(),
        dateOfBirth,
        address: address.trim(),
        state: state.trim(),
        country: country.trim(),
        username: username.trim(),
        password,
        name: `${firstName.trim()} ${lastName.trim()}`
      });
      
      if (!result.success) {
        alert(result.message || 'Signup failed. Please try again.');
        setCurrentStep(4); // Go back to account details step
      }
    }
  };

  // Header Component
  const Header = ({ title, showBack = true }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '20px 24px',
      borderBottom: '1px solid #374151'
    }}>
      {showBack && (
        <button
          onClick={prevStep}
          style={{
            background: 'none',
            border: 'none',
            color: '#3b82f6',
            cursor: 'pointer',
            marginRight: '16px',
            padding: '4px'
          }}
        >
          <ArrowLeft size={24} />
        </button>
      )}
      <h1 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: 'white',
        margin: 0
      }}>
        {title}
      </h1>
    </div>
  );

  // No scripted loading screen: the branded splash (started by App.handleSignUp)
  // covers registration + board load, in sync with mobile.

  // Step 0: Get Started
  if (currentStep === 0) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#000', color: 'white' }}>
        <Header title="Create an account" />
        
        <div style={{ 
          padding: '32px 24px',
          maxWidth: '480px',
          margin: '0 auto'
        }}>
          {/* Avatar */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '32px'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '4px solid #2563eb'
            }}>
              <span style={{ fontSize: '48px', fontWeight: 'bold', color: 'white' }}>A</span>
            </div>
          </div>

          <div style={{ marginBottom: '24px', textAlign: 'center' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>Get Started</h2>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>Sign up to get the most out of PlayerBet</p>
          </div>

          <SimpleInput
            label="First Name"
            value={firstName}
            onChange={setFirstName}
            placeholder="John"
            error={errors.firstName}
          />

          <SimpleInput
            label="Last Name"
            value={lastName}
            onChange={setLastName}
            placeholder="Doe"
            error={errors.lastName}
          />

          <SimpleInput
            label="Phone Number"
            type="tel"
            value={phoneNumber}
            onChange={setPhoneNumber}
            placeholder="+234"
            error={errors.phoneNumber}
          />

          <SimpleInput
            label="Referral Code (Optional)"
            value={referralCode}
            onChange={setReferralCode}
            placeholder="ABC123"
          />

          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '24px', lineHeight: '1.4' }}>
            By proceeding, you agree to our Terms & Conditions and Privacy Policy
          </div>

          <button
            onClick={nextStep}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 1: Basic Information with Web Date Picker
  if (currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#000', color: 'white' }}>
        <Header title="Create an account" />
        
        <div style={{ 
          padding: '32px 24px',
          maxWidth: '480px',
          margin: '0 auto'
        }}>
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>Basic Information</h2>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>Let's get to know you, shall we?</p>
          </div>

          <SimpleInput
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="john@gmail.com"
            error={errors.email}
          />

          {/* Date Picker for Web */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#e5e7eb',
              fontSize: '14px',
              marginBottom: '8px'
            }}>
              Date of Birth
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#374151',
                  border: '1px solid ' + (errors.dateOfBirth ? '#ef4444' : '#4b5563'),
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  colorScheme: 'dark'
                }}
                onFocus={(e) => e.target.style.borderColor = errors.dateOfBirth ? '#ef4444' : '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = errors.dateOfBirth ? '#ef4444' : '#4b5563'}
              />
              <Calendar 
                size={20} 
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  pointerEvents: 'none'
                }}
              />
            </div>
            {errors.dateOfBirth && (
              <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                {errors.dateOfBirth}
              </div>
            )}
          </div>

          <div style={{ padding: '12px', backgroundColor: '#7c2d12', borderRadius: '8px', marginBottom: '20px', fontSize: '12px', color: '#fca5a5' }}>
            You must be 18 or older to play
          </div>

          <SimpleInput
            label="Home address"
            value={address}
            onChange={setAddress}
            placeholder="10, Riverside Street"
            error={errors.address}
          />

          <SimpleInput
            label="State"
            value={state}
            onChange={setState}
            placeholder="Lagos"
            error={errors.state}
          />

          <SimpleInput
            label="Country"
            value={country}
            onChange={setCountry}
            placeholder="Nigeria"
          />

          <button
            onClick={nextStep}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 2: OTP Verification Setup
  if (currentStep === 2) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#000', color: 'white' }}>
        <Header title="Create an account" />
        
        <div style={{ 
          padding: '32px 24px', 
          textAlign: 'center',
          maxWidth: '480px',
          margin: '0 auto'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>OTP Verification</h2>
          <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '32px' }}>
            We have sent a verification code to<br />
            <strong style={{ color: 'white' }}>{phoneNumber}</strong>
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '32px' }}>
            {[0, 1, 2, 3, 4].map((index) => (
              <div
                key={index}
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#374151',
                  border: '2px solid #4b5563',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                {otpCode[index] || ''}
              </div>
            ))}
          </div>

          <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '32px' }}>
            Didn't receive a code? Request in 00:58
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', maxWidth: '240px', margin: '0 auto 32px' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                onClick={() => handleOtpInput(num.toString())}
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'transparent',
                  border: '2px solid #4b5563',
                  borderRadius: '50%',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#4b5563';
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                {num}
              </button>
            ))}
            <button 
              onClick={() => handleOtpInput('*')} 
              style={{ 
                width: '60px', 
                height: '60px', 
                backgroundColor: 'transparent', 
                border: '2px solid #4b5563', 
                borderRadius: '50%', 
                color: 'white', 
                fontSize: '20px', 
                cursor: 'pointer' 
              }}
            >
              *
            </button>
            <button 
              onClick={() => handleOtpInput('0')} 
              style={{ 
                width: '60px', 
                height: '60px', 
                backgroundColor: 'transparent', 
                border: '2px solid #4b5563', 
                borderRadius: '50%', 
                color: 'white', 
                fontSize: '20px', 
                fontWeight: 'bold', 
                cursor: 'pointer' 
              }}
            >
              0
            </button>
            <button 
              onClick={clearOtp} 
              style={{ 
                width: '60px', 
                height: '60px', 
                backgroundColor: 'transparent', 
                border: '2px solid #4b5563', 
                borderRadius: '50%', 
                color: 'white', 
                fontSize: '16px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ⌫
            </button>
          </div>

          <button
            onClick={verifyOtp}
            disabled={otpCode.length < 5}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: otpCode.length === 5 ? '#2563eb' : '#4b5563',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: otpCode.length === 5 ? 'pointer' : 'not-allowed',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => otpCode.length === 5 && (e.target.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => otpCode.length === 5 && (e.target.style.transform = 'translateY(0)')}
          >
            Verify
          </button>
        </div>
      </div>
    );
  }

  // Step 3: OTP Verification Success  
  if (currentStep === 3) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#000', color: 'white' }}>
        <Header title="Create an account" />
        
        <div style={{ 
          padding: '32px 24px', 
          textAlign: 'center',
          maxWidth: '480px',
          margin: '0 auto'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>OTP Verification</h2>
          <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '32px' }}>
            We have sent a verification code to<br />
            <strong style={{ color: 'white' }}>{phoneNumber}</strong>
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '32px' }}>
            {[1, 2, 3, 4, 5].map((num) => (
              <div
                key={num}
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#ef4444',
                  border: '2px solid #ef4444',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'white'
                }}
              >
                {num}
              </div>
            ))}
          </div>

          <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '32px' }}>
            Not your account? <button style={{ color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}>Log out</button>
          </p>

          <button
            onClick={nextStep}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Verify
          </button>
        </div>
      </div>
    );
  }

  // Step 4: Almost Done
  if (currentStep === 4) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#000', color: 'white' }}>
        <Header title="Create an account" />
        
        <div style={{ 
          padding: '32px 24px',
          maxWidth: '480px',
          margin: '0 auto'
        }}>
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>Almost done</h2>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>Secure your account</p>
          </div>

          <SimpleInput
            label="Username"
            value={username}
            onChange={setUsername}
            placeholder="johndoe"
            error={errors.username}
          />

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: '#e5e7eb', fontSize: '14px', marginBottom: '8px' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingRight: '48px',
                  backgroundColor: '#374151',
                  border: '1px solid ' + (errors.password ? '#ef4444' : '#4b5563'),
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = errors.password ? '#ef4444' : '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = errors.password ? '#ef4444' : '#4b5563'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer'
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                {errors.password}
              </div>
            )}
          </div>

          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '24px', lineHeight: '1.4' }}>
            • Upper and lower case letters<br />
            • Between 8 to 64 characters<br />
            • Combination of symbols
          </div>

          <div style={{ padding: '12px', backgroundColor: '#1f2937', borderRadius: '8px', marginBottom: '24px', fontSize: '12px', color: '#9ca3af', lineHeight: '1.4' }}>
            📋 By creating an account you agree to our Terms and Conditions & Privacy Policy which includes how we can use, share & store your personal information.
          </div>

          <button
            onClick={nextStep}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default SignUpFlow;